import {
  Button,
  ButtonContainer,
  Card,
  Checkbox,
  ContentSection,
  Heading,
  Icon,
  Spinner,
  Table,
} from '@vwfs-bronson/bronson-react';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  financialProductPageSliceActions,
  financialProductPageSliceSelectors,
} from '../../screens/NewQuotation/FinancialProductPage/financialProductPage.slice';
import { newQuotationSliceActions, WizardPagesEnum } from '../../screens/NewQuotation/newQuotation.slice';
import { useSearchVehicleQuery } from '../../services/redux/features/api/search-vehicle.slice';
import { journeyDataActions, journeyDataSelectors } from '../../services/redux/features/journeyData.slice';
import { useNavigate } from '../../services/routing';
import { ProductFamilyEnum } from '../../types/productFamilySelection';
import { quotesMapper } from './compareQuotes.mapper';
import { CompareQuoteTableDataModel } from './compareQuotes.model';
import { mockedQuotes } from './quotes';

export const CompareQuotes: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const headers: { [key: string]: string } = {};
  const headingsKeys = ['financeDetailsHeading', 'vehicleDetailsHeading', 'servicePlanHeading'];
  const headersKeys = [
    'financeDetailsHeading',
    'invoicePrice',
    'financeProduct',
    'campaignName',
    'totalDepositPercentage',
    'totalDepositEuro',
    'amountFinanced',
    'apr',
    'term',
    'monthlyPayment',
    'vehicleDetailsHeading',
    'brand',
    'model',
    'variant',
    'newUsed',
    'servicePlanHeading',
    'servicePlanPrice',
    'servicePlan',
    'contractDuration',
    'paymentType',
    'paymentTerm',
  ];

  const [selectedQuoteId, setSelectedQuoteId] = useState<string>();
  const [mappedQuotes, setMappedQuotes] = useState<Array<CompareQuoteTableDataModel>>([]);

  const isAdditionalServicePlanAdded = useSelector(journeyDataSelectors.isAdditionalServicePlanAdded) || false;
  const vehicleType = useSelector(journeyDataSelectors.vehicleType)!;
  const isDisplayedQuotesCountLimitReached = mappedQuotes.length <= 2;

  const { isLoading, data: usedCarData } = useSearchVehicleQuery();

  const numberOfChosenQuotes = useSelector(journeyDataSelectors.numberOfChosenQuotes) || 0;
  const numberOfChosenQuotesLocked = useSelector(journeyDataSelectors.numberOfChosenQuotesLocked);
  const isServicePlan = useSelector(journeyDataSelectors.productFamily) === ProductFamilyEnum.servicePlan;
  const slicedMockedQuotes = mockedQuotes.slice(0, numberOfChosenQuotes);
  const shouldRenderAddAdditionalQuoteButton =
    numberOfChosenQuotes < 3 && !numberOfChosenQuotesLocked && !isServicePlan;

  headersKeys.forEach((headerKey) => {
    headers[headerKey] = t(`compareQuotes:header.${headerKey}`);
  });

  const quotes = useSelector(financialProductPageSliceSelectors.quotes);

  useEffect(() => {
    if (usedCarData) {
      setMappedQuotes(quotesMapper(quotes, isAdditionalServicePlanAdded, usedCarData, vehicleType));
    }
  }, [quotes, isAdditionalServicePlanAdded, vehicleType, usedCarData]);

  const editSelectedQuote = () => {
    // PC-3728 - needed to comment this if block so we can still use the navigation buttons
    // if (selectedQuoteId) {
    // actions.financialProductPage.loadQuoteForEditing({ id: selectedQuoteId });
    dispatch(newQuotationSliceActions.wizardNavigateToPage(WizardPagesEnum.financialProduct));
    // }
  };

  const copySelectedQuote = () => {
    if (selectedQuoteId) {
      dispatch(financialProductPageSliceActions.copyQuote({ id: selectedQuoteId }));
    }
  };

  const deleteSelectedQuote = () => {
    if (selectedQuoteId) {
      dispatch(financialProductPageSliceActions.deleteQuote({ id: selectedQuoteId }));
    }
  };

  const isAnyQuoteSelected = () => {
    return !!selectedQuoteId;
  };

  const valueWithIcon = (quoteIndex, headerKey) => {
    return slicedMockedQuotes[quoteIndex][`${headerKey}Valid`] ? (
      <span className="u-text-success">
        {slicedMockedQuotes[quoteIndex][headerKey]} <Icon name="semantic-success" />
      </span>
    ) : (
      <span className="u-text-alert">
        {slicedMockedQuotes[quoteIndex][headerKey]} <Icon name="semantic-error" />
      </span>
    );
  };

  const renderContinueButtonTitle = (numberOfChosenQuotes: number, numberOfChosenQuotesLocked: boolean = false) => {
    if (numberOfChosenQuotesLocked || numberOfChosenQuotes === 1) {
      return t('compareQuotes:continueWithOne');
    }

    return t('compareQuotes:continueWithMultiple');
  };

  const onContinue = () => {
    if (numberOfChosenQuotesLocked) {
      dispatch(newQuotationSliceActions.wizardNavigateToPage(WizardPagesEnum.customerData));
    } else {
      dispatch(journeyDataActions.setNumberOfChosenQuotesLocked(true));
      dispatch(newQuotationSliceActions.wizardNextPage());
    }
  };

  return (
    <Spinner busy={isLoading} section>
      <ContentSection pageWrapSize="small">
        <Heading level={2}>{t('compareQuotes:title')}</Heading>
        <Card className="u-text-left" title="">
          <Table>
            <React.Fragment key=".0">
              <Table.Thead>
                <Table.Tr>
                  {numberOfChosenQuotesLocked && (
                    <>
                      <Table.Th />
                      {slicedMockedQuotes.map((quote) => (
                        <Table.Th key={`quote-${quote.quoteNumber}`}>
                          <Checkbox
                            name={`quote-${quote.quoteNumber}`}
                            checked={selectedQuoteId === quote.quoteNumber}
                            onChange={() => {
                              setSelectedQuoteId(quote.quoteNumber);
                            }}
                          >
                            {`${quote.quoteNumber}/${quote.quoteVersion}`}
                          </Checkbox>
                        </Table.Th>
                      ))}
                    </>
                  )}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Object.entries(headers).map(([headerKey, headerName]) => {
                  if (headingsKeys.includes(headerKey)) {
                    if (headerKey === 'servicePlanHeading') {
                      return (
                        <Table.Tr key={headerKey} selected>
                          <Table.Th>{headerName}</Table.Th>
                          {Array.from(slicedMockedQuotes.keys()).map((quoteIndex) => (
                            <Table.Th key={`${headerKey}-${quoteIndex + 1}`}>
                              {`${slicedMockedQuotes[quoteIndex].quoteNumber}/${slicedMockedQuotes[quoteIndex].quoteVersion}`}
                            </Table.Th>
                          ))}
                        </Table.Tr>
                      );
                    }
                    return (
                      <Table.Tr key={headerKey} selected>
                        <Table.Th colSpan={slicedMockedQuotes.length + 1}>{headerName}</Table.Th>
                      </Table.Tr>
                    );
                  }

                  return (
                    <Table.Tr key={headerKey}>
                      <Table.Th>{headerName}</Table.Th>
                      {Array.from(slicedMockedQuotes.keys()).map((quoteIndex) => (
                        <Table.Td key={`${headerKey}-${quoteIndex + 1}`}>
                          {['monthlyPayment', 'servicePlanPrice'].includes(headerKey)
                            ? valueWithIcon(quoteIndex, headerKey)
                            : slicedMockedQuotes[quoteIndex][headerKey]}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </React.Fragment>
          </Table>
          {numberOfChosenQuotesLocked && (
            <ButtonContainer center>
              <Button secondary disabled={!isAnyQuoteSelected()} onClick={editSelectedQuote}>
                {t('compareQuotes:edit')}
              </Button>
              <Button
                secondary
                disabled={!isAnyQuoteSelected() || !isDisplayedQuotesCountLimitReached}
                onClick={copySelectedQuote}
              >
                {t('compareQuotes:copy')}
              </Button>
              <Button
                secondary
                disabled={!isAnyQuoteSelected() || mappedQuotes.length <= 1}
                onClick={deleteSelectedQuote}
              >
                {t('compareQuotes:delete')}
              </Button>
            </ButtonContainer>
          )}
        </Card>
        <br />
        <ButtonContainer center>
          <Button secondary onClick={editSelectedQuote}>
            {t('compareQuotes:back')}
          </Button>
          <Button secondary disabled={!isAnyQuoteSelected() && numberOfChosenQuotesLocked} onClick={onContinue}>
            {renderContinueButtonTitle(numberOfChosenQuotes, numberOfChosenQuotesLocked)}
          </Button>

          {shouldRenderAddAdditionalQuoteButton && (
            <Button
              secondary
              onClick={() => {
                dispatch(journeyDataActions.increaseNumberOfChosenQuotes());
                navigate.financialProduct();
              }}
            >
              {t('compareQuotes:add')}
            </Button>
          )}
        </ButtonContainer>
      </ContentSection>
    </Spinner>
  );
};
